import {
  Button,
  DialogV2,
  Dropdown,
  DropdownRef,
  Input,
  Loader,
  Menu,
  Typography,
} from '@recruit-robin/robin-theme/web-components';
import { AlertTriangle, ChevronDown, ChevronUp } from '@recruit-robin/robin-theme/web-icons';
import { useCallback, useContext, useRef, useState } from 'react';

import type { Vacancy } from 'clients/AutoCompleteClient/types';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { VacancyList } from 'components/ui/molecules/VacancyList/VacancyList';
import { useStyles } from './SelectVacancyFromListModal.styles';
import type { SelectVacancyFromListModalProps } from './SelectVacancyFromListModal.types';

export const SelectVacancyFromListModal = ({
  isVisible = true,
  onClose,
  icon,
  title,
  subtitle,
  onSearchChange,
  vacancyList,
  selectedVacancy,
  onSelectVacancy,
  onConfirm,
  isLoading = false,
  isVacanciesLoading = false,
  footer,
  errorMessage,
  cancelButtonLabel,
  confirmButtonLabel = 'Confirm',
  onCloseButtonClick,
}: SelectVacancyFromListModalProps) => {
  const classes = useStyles({});
  const { dictionary } = useContext(LocalizationContext);
  const dropdownRef = useRef<DropdownRef>(null);
  const [searchValue, setSearchValue] = useState('');

  const handleSelect = useCallback(
    (vacancy: Vacancy | null) => {
      onSelectVacancy(vacancy);
      dropdownRef.current?.close();
    },
    [onSelectVacancy],
  );

  const renderEndIcon = useCallback(
    (isOpen: boolean) => (
      <div css={classes.endIcon}>
        {isVacanciesLoading ? <Loader size={14} /> : isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </div>
    ),
    [classes.endIcon, isVacanciesLoading],
  );

  const dropdownValueRenderer = useCallback(
    ({ isOpen }: { isOpen: boolean }) => (
      <Input
        disabled={isVacanciesLoading}
        endIcon={renderEndIcon(isOpen)}
        onInput={(event) => {
          const { value } = event.currentTarget;
          setSearchValue(value);
          onSearchChange(value);
        }}
        value={isOpen ? searchValue : selectedVacancy ? selectedVacancy.name ?? selectedVacancy.value ?? '' : ''}
        contained
        placeholder={dictionary.searchVacancy}
      />
    ),
    [onSearchChange, renderEndIcon, searchValue, selectedVacancy, dictionary.searchVacancy, isVacanciesLoading],
  );

  const handleConfirm = useCallback(() => {
    if (!selectedVacancy) {
      return;
    }

    onConfirm();
  }, [onConfirm, selectedVacancy]);

  return (
    <DialogV2.Root isVisible={isVisible} customCss={classes.dialog}>
      <DialogV2.Backdrop />
      <DialogV2.Content customCss={classes.content}>
        {onCloseButtonClick && <DialogV2.Close onRequestClose={onCloseButtonClick} />}
        <DialogV2.Header customCss={classes.title}>
          {icon}
          <Typography variant={(v) => v.heading[3]} color={(c) => c.neutral.variant[80]}>
            {title}
          </Typography>
        </DialogV2.Header>

        <DialogV2.Body>
          <div css={classes.body}>
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
              {subtitle}
            </Typography>

            <Dropdown
              placement="bottom"
              flipMenu={false}
              ref={dropdownRef}
              css={classes.selectWrapper}
              renderMenu={
                <Menu.Root customCss={classes.select}>
                  <VacancyList
                    selectedVacancy={selectedVacancy}
                    onSelectVacancy={(vacancy) => handleSelect(vacancy)}
                    onCreateJob={() => {}}
                    vacanciesList={vacancyList}
                  />
                </Menu.Root>
              }>
              {dropdownValueRenderer}
            </Dropdown>

            {errorMessage && (
              <div css={classes.errorMessage}>
                <AlertTriangle size={16} color={(c) => c.tertiary.red[60]} />
                <Typography variant={(v) => v.body.short} color={(c) => c.tertiary.red[60]}>
                  {errorMessage}
                </Typography>
              </div>
            )}

            {footer && <div css={classes.footerMessageContainer}>{footer}</div>}
          </div>
        </DialogV2.Body>

        <DialogV2.Footer>
          {cancelButtonLabel && <Button label={cancelButtonLabel} onClick={onClose} style="text" type="subtle2" />}
          <Button
            label={confirmButtonLabel}
            onClick={handleConfirm}
            style="filled"
            type="success"
            disabled={!selectedVacancy || isLoading}
            startIcon={isLoading ? <Loader size={16} /> : undefined}
          />
        </DialogV2.Footer>
      </DialogV2.Content>
    </DialogV2.Root>
  );
};
